import isNull from 'lodash/isNull';
import { useTracking } from 'react-tracking';

import { ROUTE_DEPRECATED_PORTFOLIO } from '@/constants/routes';

import { Tile, TileLink } from '@/components/DeprecatedTile';
import { slugify } from '@/utilities/slugify';
import {
  getCloudflareImageUrl,
  getResponsiveImage,
  getStaticUrl,
} from '@/hooks/useCloudflareImage';
import {
  getMarketCoverFromCurrency,
  getProviderImageFromName,
} from '@/utilities/coverImages';

import type { Portfolio } from '@/hooks/useDeprecatedUserPortfolios';

type Props = {
  index: number;
  portfolio: Portfolio;
};

export const UserPortfolioTile = ({ index, portfolio }: Props) => {
  const { trackEvent } = useTracking();
  const {
    id,
    name,
    currencyISO,
    holdingsCount,
    formattedDateGenerated,
    provider,
  } = portfolio;
  const safeHoldingsCount = holdingsCount || 0;
  const createdString = formattedDateGenerated
    ? `Created: ${formattedDateGenerated},  `
    : '';
  const isLinkedPortfolio = !isNull(provider) && provider !== '';
  const linkedToString = isLinkedPortfolio ? `Linked to: ${provider}` : '';
  const descriptionString = isLinkedPortfolio
    ? linkedToString
    : `${createdString}Includes: ${safeHoldingsCount} holding${
        safeHoldingsCount === 1 ? '' : 's'
      }`;

  let coverImgSrc = getStaticUrl(`blank.jpg`);

  if (typeof provider === 'string') {
    coverImgSrc = getProviderImageFromName(provider);
  } else {
    coverImgSrc = getMarketCoverFromCurrency(currencyISO);
  }

  const imageUrl = getCloudflareImageUrl({
    src: coverImgSrc,
    width: 276,
    height: 188,
    fit: 'crop',
  });

  const responsiveImages = getResponsiveImage({
    src: coverImgSrc,
    breakpoints: [
      { width: 276, height: 188, fit: 'crop' },
      { width: 163, height: 90, fit: 'crop' },
    ],
  });

  const portfolioName = `${name} (${currencyISO})`;
  return (
    <TileLink
      data-cy-id={`portfolio-link-${id}`}
      inheritColor
      noUnderline
      to={{
        type: ROUTE_DEPRECATED_PORTFOLIO,
        payload: {
          portfolioId: id,
          name: slugify(name) || undefined,
        },
      }}
      onClick={() => {
        trackEvent({
          action: 'click',
          subject: 'tile',
          portfolioId: id,
          index: index,
        });
      }}
    >
      <Tile
        name={portfolioName}
        coverImage={imageUrl}
        coverImageSet={responsiveImages.srcSet}
        coverFit={isLinkedPortfolio ? 'contain' : 'cover'}
        description={descriptionString}
      />
    </TileLink>
  );
};
